import { getBrandConfig } from '../config/brands';
import { resolveBrandKey } from './brandRouting';

type StorageShimRule = {
  test: (key: string) => boolean;
  transform: (key: string) => string;
};

const resolveCurrentBrandKey = () =>
  resolveBrandKey(window.location.pathname, window.location.hostname);

const withCurrentBrandPrefix = (suffix: string) => {
  const { storagePrefix } = getBrandConfig(resolveCurrentBrandKey());
  return `${storagePrefix}_${suffix}`;
};

const withSpecificBrandPrefix = (brandKey: string, suffix: string) => {
  const { storagePrefix } = getBrandConfig(brandKey);
  return `${storagePrefix}_${suffix}`;
};

const matchesAnyExactKey = (key: string, exactKeys: string[]) => exactKeys.includes(key);

const STORAGE_SHIM_RULES: StorageShimRule[] = [
  {
    test: (key) => key === 'us_citrus_ai_brand_kit' || key === 'us_citrus_brand_kit',
    transform: () => withCurrentBrandPrefix('brand_kit'),
  },
  {
    test: (key) => key === 'us_citrus_ai_brand_kit_draft' || key === 'us_citrus_brand_kit_draft',
    transform: () => withCurrentBrandPrefix('brand_kit_draft'),
  },
  {
    test: (key) => key === 'us_citrus_ai_sitemap_data' || key === 'us_citrus_sitemap_data',
    transform: () => withCurrentBrandPrefix('sitemap_data'),
  },
  {
    test: (key) => key === 'us_citrus_ai_voc_kit' || key === 'us_citrus_voc_kit',
    transform: () => withCurrentBrandPrefix('voc_kit'),
  },
  {
    test: (key) => key === 'us_citrus_ai_voc_kit_draft' || key === 'us_citrus_voc_kit_draft',
    transform: () => withCurrentBrandPrefix('voc_kit_draft'),
  },
  {
    test: (key) => key === 'us_citrus_ai_voc_kit_ready_dismissed' || key === 'us_citrus_voc_kit_ready_dismissed',
    transform: () => withCurrentBrandPrefix('voc_kit_ready_dismissed'),
  },
  {
    test: (key) => key === 'us_citrus_ai_paa_trees_v3',
    transform: () => withCurrentBrandPrefix('paa_trees_v3'),
  },
  {
    test: (key) => key === 'us_citrus_ai_rewrite_blog_posts_progress_v2',
    transform: () => withCurrentBrandPrefix('rewrite_blog_posts_progress_v2'),
  },
  {
    test: (key) => key === 'us_citrus_blog_agents_progress',
    transform: () => withCurrentBrandPrefix('blog_agents_progress'),
  },
  {
    test: (key) => key === 'us_citrus_ai_email_newsletters',
    transform: () => withCurrentBrandPrefix('email_newsletters'),
  },
  {
    test: (key) => key === 'us_citrus_ai_generated_comments',
    transform: () => withCurrentBrandPrefix('generated_comments'),
  },
  {
    test: (key) =>
      matchesAnyExactKey(key, [
        'us_citrus_ai_playbook_spreadsheet_id',
        'us_citrus_ai_reddit_blog_spreadsheet_id',
        'us_citrus_ai_blog_spreadsheet_id',
        'us_citrus_ai_competitor_spreadsheet_id',
      ]),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_ai_/, '')),
  },
  {
    test: (key) =>
      matchesAnyExactKey(key, [
        'us_citrus_ai_voc_agent_data',
        'us_citrus_ai_voc_agent_draft',
        'us_citrus_ai_competitor_conquesting_progress',
        'us_citrus_ai_blog_creator_progress',
        'us_citrus_ai_reddit_analysis_cache',
        'us_citrus_ai_content_cache',
      ]),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_ai_/, '')),
  },
  {
    test: (key) => /^us_citrus_ai_rewrite_blog_(uscitrusnursery|uscitrus|drmanismagic|shoproyalgold)_data_v2$/.test(key),
    transform: (key) => {
      const match = key.match(/^us_citrus_ai_rewrite_blog_(uscitrusnursery|uscitrus|drmanismagic|shoproyalgold)_data_v2$/);
      return withSpecificBrandPrefix(match?.[1] || resolveCurrentBrandKey(), 'rewrite_blog_dataset_v2');
    },
  },
  {
    test: (key) => /^(us_citrus_ai|us_citrus)_competitor_workflow_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^(us_citrus_ai|us_citrus)_/, '')),
  },
  {
    test: (key) => /^(us_citrus_ai|us_citrus)_competitor_meta_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^(us_citrus_ai|us_citrus)_/, '')),
  },
  {
    test: (key) => /^(us_citrus_ai|us_citrus)_competitor_prompts_draft_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^(us_citrus_ai|us_citrus)_/, '')),
  },
  {
    test: (key) => /^us_citrus_ai_competitor_ctas_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_ai_/, '')),
  },
  {
    test: (key) => /^us_citrus_blog_content_draft_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_/, '')),
  },
  {
    test: (key) => /^us_citrus_blog_prompts_draft_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_/, '')),
  },
  {
    test: (key) => /^us_citrus_blog_meta_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_/, '')),
  },
  {
    test: (key) => /^us_citrus_cta_creator_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_/, '')),
  },
  {
    test: (key) => /^us_citrus_linkedin_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_/, '')),
  },
  {
    test: (key) => /^us_citrus_playbook_/.test(key),
    transform: (key) => withCurrentBrandPrefix(key.replace(/^us_citrus_/, '')),
  },
];

const toScopedKey = (key: string) => {
  const matchedRule = STORAGE_SHIM_RULES.find((rule) => rule.test(key));
  return matchedRule ? matchedRule.transform(key) : key;
};

/**
 * Redirect legacy storage keys into the active brand namespace.
 * Why this matters: it prevents old US Citrus AI/USCN key lookups from leaking data
 * between brands while the rest of the codebase is being migrated incrementally.
 */
export const installLegacyBrandStorageShim = (): void => {
  const storageProto = Storage.prototype as Storage & {
    __brandShimInstalled?: boolean;
    __brandGetItem?: Storage['getItem'];
    __brandSetItem?: Storage['setItem'];
    __brandRemoveItem?: Storage['removeItem'];
  };

  if (storageProto.__brandShimInstalled) {
    return;
  }

  storageProto.__brandShimInstalled = true;
  storageProto.__brandGetItem = storageProto.getItem;
  storageProto.__brandSetItem = storageProto.setItem;
  storageProto.__brandRemoveItem = storageProto.removeItem;

  storageProto.getItem = function patchedGetItem(key: string) {
    return storageProto.__brandGetItem!.call(this, toScopedKey(key));
  };

  storageProto.setItem = function patchedSetItem(key: string, value: string) {
    return storageProto.__brandSetItem!.call(this, toScopedKey(key), value);
  };

  storageProto.removeItem = function patchedRemoveItem(key: string) {
    return storageProto.__brandRemoveItem!.call(this, toScopedKey(key));
  };
};
